import {
    ComponentRef,
    Directive,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    NgZone,
    Output,
    Renderer2,
    Type,
    ViewContainerRef,
} from '@angular/core';
import {UserAuthService} from '../../../../@core/user-auth.service';
import {SlidePanelService} from '../../../slide-panel/slide-panel.service';
import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {getScrollParent} from '../../../../utils/dom.utils';
import {Router} from '@angular/router';
import {UnsubscribeComponent} from '../../../../@core/fc-component';
import {createComponent} from '../../../../utils/component.utils';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {HoverComponentRegistryEntry, HoverComponentRegistryService} from '../services/hover-component-registry.service';

export const HOVER_DETAILS_DELAY = 500;
export const HOVER_DETAILS_HIDE_DELAY = 200;
export type HoverComponentPosition = 'top-right' | 'middle-right' | 'top-left' | 'middle-left' | 'auto';

@Directive({
    selector: '[hoverComponent]',
})
export class HoverComponentDirective<T = any> extends UnsubscribeComponent {
    protected hoverRegistry = inject(HoverComponentRegistryService);
    private static cancelHideUpChain(directive: HoverComponentDirective<any>): void {
        let current: HoverComponentDirective<any> | null = directive;
        while (current) {
            current.clearHideTimer();
            current = current._parent;
        }
    }

    private static scheduleHideUpChain(directive: HoverComponentDirective<any>, delay: number): void {
        let current: HoverComponentDirective<any> | null = directive;
        while (current) {
            current.scheduleHide(delay);
            current = current._parent;
        }
    }

    @Input('hoverComponent') component: Type<any>;
    @Input() data: T;
    @Input() position: HoverComponentPosition = 'auto';
    @Input() hoverElementExtraStyles: {[property: string]: string};
    @Input() hoverElementExtraClasses: string[];
    @Input() showDelay: number = HOVER_DETAILS_DELAY;
    @Input() hideDelay: number = HOVER_DETAILS_HIDE_DELAY;
    @Input() identityKey: unknown;
    @Output('destroyed') destroyed = new EventEmitter<void>();
    @Output('created') created = new EventEmitter<void>();
    componentRef: ComponentRef<any> = null;
    activeModals$ = this.modalService.activeInstances;

    onMouseEnter = () => {
        this.zone.runOutsideAngular(() => {
            this.clearHideTimer();
            this._timer = setTimeout(() => this.zone.run(() => this.createComponent()), this.showDelay);
        });
    };

    onMouseLeave = () => {
        this.zone.runOutsideAngular(() => {
            this.scheduleHide(this.hideDelay);
        });
    };

    _user; // accessed by subclass
    private _timer: ReturnType<typeof setTimeout> | null = null;
    private _removeEnterListener: () => void;
    private _removeLeaveListener: () => void;
    private _intersectionObserver: IntersectionObserver;
    private _resizeObserver: ResizeObserver;
    private _destroyComp$ = new Subject<null>();
    private _previewHovered = false;
    private _parent: HoverComponentDirective<any> | null = null;

    constructor(private el: ElementRef,
                private vc: ViewContainerRef,
                private renderer: Renderer2,
                private authService: UserAuthService,
                private zone: NgZone,
                protected slidePanelService: SlidePanelService,
                private modalService: NgbModal,
                public router: Router) {
        super();

        this.authService.user.pipe(take(1)).subscribe(user => {
            this._user = user;
            this.destroyComponent();
        });

        this.zone.runOutsideAngular(() => {
            this.el.nativeElement.addEventListener('mouseenter', this.onMouseEnter);
            this.el.nativeElement.addEventListener('mouseleave', this.onMouseLeave);
        });
    }

    destroyFromGlobal(): void {
        this.clearHideTimer();
        this._previewHovered = false;
        this.destroyComponent();
    }

    compOnDestroy(): void {
        this.el.nativeElement.removeEventListener('mouseenter', this.onMouseEnter);
        this.el.nativeElement.removeEventListener('mouseleave', this.onMouseLeave);
        this.clearHideTimer();
        if (!this._previewHovered) {
            this.destroyComponent();
        } else {
            this.hoverRegistry.forEach((d: HoverComponentRegistryEntry) => {
                const directive = d as HoverComponentDirective<any>;
                if (directive._parent === this) directive._parent = null;
            });
            this._parent = null;

            this._intersectionObserver?.disconnect();
            this._intersectionObserver = null;
            this._resizeObserver?.disconnect();
            this._resizeObserver = null;

            const previewElement = this.componentRef?.location?.nativeElement as HTMLElement | undefined;
            this._removeEnterListener?.();
            this._removeEnterListener = null;
            this._removeLeaveListener?.();
            this._removeLeaveListener = null;
            if (!previewElement) return;
            this._removeLeaveListener = this.renderer.listen(previewElement, 'mouseleave', () => {
                this.finalCleanup();
            });
        }
    }

    createComponent(beforeRender?: () => void) {
        if (!this._user || this.componentRef || !this.component) return;

        const scrollParent = getScrollParent(this.el.nativeElement);
        if (!scrollParent) return;

        const ancestor = this.hoverRegistry.findAncestor(this.el.nativeElement) as HoverComponentDirective<any> | null;
        // Intentionally dedupe by preview kind (class-level), not by concrete record instance.
        if (this.isDuplicateOfAncestor(ancestor)) return;

        this._destroyComp$.complete();
        this._destroyComp$ = new Subject<null>();
        this._destroyComp$
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(() => this.destroyComponent());

        this.componentRef = createComponent(this.component, this.vc);
        this.componentRef.instance.data = this.data;

        if (beforeRender) beforeRender();

        const previewElement = this.componentRef.location.nativeElement;
        previewElement.style.position = 'absolute';
        previewElement.style.visibility = 'hidden';
        previewElement.style.pointerEvents = 'none';
        this.renderer.appendChild(document.body, previewElement);
        this._parent = ancestor;
        this.hoverRegistry.add(this as HoverComponentRegistryEntry);

        this._intersectionObserver = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting && previewElement.style.visibility === 'visible') {
                    this.zone.run(() => this._destroyComp$.next(null));
                }
            },
            {root: scrollParent},
        );
        this._intersectionObserver.observe(this.el.nativeElement);

        // Positioned via ResizeObserver so real dimensions are always used —
        // the inner component loads async, dimensions start at 0.
        // NOTE: ResizeObserver callbacks fire outside NgZone. positionPreview()
        // only mutates inline DOM styles, so Angular change detection is not needed.
        // If future logic updates Angular-bound state here, wrap it in zone.run(...)
        // or trigger change detection explicitly.
        this._resizeObserver = new ResizeObserver(() =>
            this.positionPreview(previewElement, scrollParent),
        );
        this._resizeObserver.observe(previewElement);

        this._removeEnterListener = this.renderer.listen(previewElement, 'mouseenter', () => {
            this._previewHovered = true;
            HoverComponentDirective.cancelHideUpChain(this);
        });
        this._removeLeaveListener = this.renderer.listen(previewElement, 'mouseleave', () => {
            this._previewHovered = false;
            HoverComponentDirective.scheduleHideUpChain(this, this.hideDelay);
        });

        this.componentRef.instance.position = this.position;
        this.componentRef.instance.selfDestroy$ = this._destroyComp$;
        this.created.emit();
        this.componentRef.changeDetectorRef.markForCheck();
    }

    protected getIdentityKey(): unknown {
        if (this.identityKey !== undefined) return this.identityKey;

        const data = this.data;
        if (data === null || data === undefined) return this.component;
        if (typeof data !== 'object') return this.component;

        // Class-level identity is intentional to prevent recursive same-kind preview chains.
        return data.constructor ?? this.component;
    }

    destroyComponent() {
        this.clearHideTimer();
        this._previewHovered = false;
        this.hoverRegistry.delete(this as unknown as HoverComponentRegistryEntry);
        this.hoverRegistry.forEach((d: HoverComponentRegistryEntry) => {
            const directive = d as HoverComponentDirective<any>;
            if (directive._parent === this) directive._parent = null;
        });
        this._parent = null;
        this.destroyed.emit();
        if (!this.componentRef) return;

        this.cleanupBubble();
        this.componentRef.destroy();
        this.componentRef = null;
        this.vc.clear();
    }

    private cleanupBubble(): void {
        this._intersectionObserver?.disconnect();
        this._intersectionObserver = null;
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
        this._removeEnterListener?.();
        this._removeEnterListener = null;
        this._removeLeaveListener?.();
        this._removeLeaveListener = null;

        const previewElement = this.componentRef?.location?.nativeElement as HTMLElement | undefined;
        previewElement?.parentNode?.removeChild(previewElement);
    }

    private finalCleanup(): void {
        this.cleanupBubble();
        this.componentRef?.destroy();
        this.componentRef = null;
        this.vc.clear();
        this.hoverRegistry.delete(this as unknown as HoverComponentRegistryEntry);
        this.destroyed.emit();
    }

    private clearHideTimer(): void {
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }
    }

    private scheduleHide(delay: number): void {
        this.clearHideTimer();
        this._timer = setTimeout(() => this.zone.run(() => this.destroyComponent()), delay);
    }

    private isDuplicateOfAncestor(start: HoverComponentDirective<any> | null): boolean {
        const identityKey = this.getIdentityKey();
        let current = start;

        while (current) {
            if (this.identityEquals(identityKey, current.getIdentityKey())) return true;
            current = current._parent;
        }

        return false;
    }

    private identityEquals(a: unknown, b: unknown): boolean {
        return a === b;
    }

    private positionPreview(previewElement: HTMLElement, scrollParent: Element): void {
        if (!this.componentRef) return;

        const previousTop = parseFloat(previewElement.style.top);
        const wasPositioned = previewElement.style.visibility === 'visible' && Number.isFinite(previousTop);
        const w = previewElement.offsetWidth;
        const h = previewElement.offsetHeight;
        if (w === 0 || h === 0) return;

        const trigger = this.el.nativeElement.getBoundingClientRect();
        const spRight = scrollParent.getClientRects()[0]?.right ?? window.innerWidth;
        const spaceOnRight = spRight - trigger.right;

        let top: number;
        let left: number;

        switch (this.position) {
            case 'auto':
                top = trigger.top > window.innerHeight / 2 ?
                    trigger.top - h + window.scrollY + trigger.height :
                    trigger.top + window.scrollY;
                left = spaceOnRight > w ?
                    trigger.left + trigger.width :
                    trigger.left + spRight - (trigger.left + w);
                break;
            case 'top-right':
                top = trigger.top + window.scrollY;
                left = trigger.left + trigger.width;
                break;
            case 'middle-right':
                top = trigger.top + trigger.height / 2 + window.scrollY;
                left = trigger.left + trigger.width;
                break;
            case 'top-left':
                top = trigger.top + window.scrollY;
                left = trigger.left;
                break;
            case 'middle-left':
                top = trigger.top + trigger.height / 2 + window.scrollY;
                left = trigger.left;
                break;
            default:
                return;
        }

        const overflowRight = window.innerWidth - left - w;
        left = overflowRight < 0 ? left + overflowRight : Math.max(0, left);

        const overflowBottom = window.innerHeight + window.scrollY - top - h;
        top = overflowBottom < 0 ? top + overflowBottom : Math.max(window.scrollY, top);

        // Content can resize after the bubble is shown (for example, after async
        // data or filtering). Keep its established top edge when it shrinks so it
        // cannot move out from under the pointer. It may still move upward when
        // growing content would otherwise overflow the viewport.
        if (wasPositioned) top = Math.min(previousTop, top);

        if (this.hoverElementExtraStyles) {
            Object.assign(previewElement.style, this.hoverElementExtraStyles);
        }

        if (this.hoverElementExtraClasses) {
            previewElement.classList.add(...this.hoverElementExtraClasses);
        }

        previewElement.style.top = `${top}px`;
        previewElement.style.left = `${left}px`;
        previewElement.style.visibility = 'visible';
        previewElement.style.pointerEvents = '';
    }
}
